/* SUMMARY
  * Imports
  * TablePageSizePreference: choisir le nombre de lignes par page de tous les tableaux
*/

/* Imports */
import { useState } from "react";
import { t } from "i18next";
import { Select, Option } from "@material-tailwind/react";
import toast from "react-hot-toast";
import {
  DEFAULT_TABLE_PAGE_SIZE,
  TABLE_PAGE_SIZE_OPTIONS,
  readTablePageSize
} from "../../services/preferences/tablePageSize";
import { saveTablePageSize } from "../../services/preferences/userPreferences";
/***/

/** Valeur de la ligne « par défaut ». Une sentinelle plutôt que "", que le Select traite comme vide. */
const DEFAULT_VALUE = "default";

/**
* TablePageSizePreference: la liste déroulante du profil qui pilote tous les tableaux paginés.
*
* Material Tailwind plutôt que react-select, comme le sélecteur de langue posé juste à côté : deux
* contrôles voisins dans la même carte doivent se ressembler. react-select sert ici aux
* multi-sélections à cases des tiroirs de filtres, ce qui n'est pas le geste demandé.
*
* Pas de `window.location.reload()` — contrairement au changement de langue, où i18next lie ses
* ressources au rendu. Une taille de page est relue par les tableaux montés dès que le miroir change.
*
* La valeur affichée est lue une seule fois à l'initialisation : c'est l'utilisateur qui la modifie
* depuis cet écran, donc rien d'autre ne peut la faire bouger sous ses pieds pendant qu'il la
* regarde.
*/
export default function TablePageSizePreference() {
  const [selected, setSelected] = useState<number>(() => readTablePageSize());
  const [isSaving, setIsSaving] = useState(false);

  const handleChange = async (value?: string) => {
    if (!value || isSaving) return;

    const size = value === DEFAULT_VALUE ? null : Number.parseInt(value, 10);
    const previous = selected;

    // Optimiste : la liste se ferme sur la valeur choisie sans attendre le réseau.
    setSelected(size ?? DEFAULT_TABLE_PAGE_SIZE);
    setIsSaving(true);

    try {
      await saveTablePageSize(size);
      toast.success(t("common:tablePageSizeUpdated"));
    } catch (error) {
      console.error("Erreur lors de l'enregistrement de la taille de page:", error);
      // Revenir en arrière : laisser la liste sur un choix que le serveur a refusé ferait croire à
      // l'utilisateur qu'il est enregistré, et il le retrouverait perdu au prochain navigateur.
      setSelected(previous);
      toast.error(t("common:tablePageSizeUpdateError"));
    } finally {
      setIsSaving(false);
    }
  };

  const currentValue = selected === DEFAULT_TABLE_PAGE_SIZE ? DEFAULT_VALUE : String(selected);
  const currentLabel = selected === DEFAULT_TABLE_PAGE_SIZE
    ? t("common:tablePageSizeDefault", { size: DEFAULT_TABLE_PAGE_SIZE })
    : String(selected);

  return (
    <div className="w-56">
      {/* `selected` rend le libellé du déclencheur nous-mêmes : laissé à Material Tailwind, il
        * n'apparie pas une valeur numérique et affiche un champ vide. C'est le contournement que
        * SwitchLanguage fait en détournant `label`, ce qui dupliquerait ici l'intitulé du champ. */}
      <Select
        value={currentValue}
        selected={() => currentLabel}
        onChange={handleChange}
        disabled={isSaving}
        className="bg-white"
      >
        <Option value={DEFAULT_VALUE}>
          {t("common:tablePageSizeDefault", { size: DEFAULT_TABLE_PAGE_SIZE })}
        </Option>

        {TABLE_PAGE_SIZE_OPTIONS.map((size) => (
          <Option key={size} value={String(size)}>{String(size)}</Option>
        ))}
      </Select>
    </div>
  );
}
/***/
